import React, { useState } from "react";
import { Link } from "../router";
import PageHero from "../components/PageHero";

const STEPS = [
  ["01", "List in minutes", "Upload your asset, blueprints and media, and set your expected price — all from a clean, premium seller dashboard."],
  ["02", "We vet it", "Our legal team runs the Aditi Certified Title Check. Cleared assets go live with a verified timestamp; nothing else publishes."],
  ["03", "Reach real buyers", "No spam, no tyre-kickers. We position your asset only to matched, intent-verified buyers and route financing directly."],
];

export default function SellPage() {
  const [sent, setSent] = useState(false);

  return (
    <>
      <PageHero
        eyebrow="Seller Portal · For owners & institutions"
        title="List once. Sell to"
        italic="serious buyers."
        sub="A premium dashboard to list properties, upload blueprints and media, and name your price — without the spammy clutter of an ordinary portal."
        tone={["#0B0B0C", "#101c18"]}
      />

      {/* How it works */}
      <section className="section">
        <div className="shell">
          <div className="reveal" style={{ maxWidth: 620, marginBottom: 60 }}>
            <span className="eyebrow">How selling works</span>
            <h2 className="h-xl" style={{ marginTop: 22 }}>Three steps.<br />Zero noise.</h2>
          </div>
          <div className="grid" style={{ gridTemplateColumns: "repeat(3,1fr)", gap: "clamp(28px, 3vw, 48px)" }}>
            {STEPS.map(([no, t, b]) => (
              <div key={no} className="reveal" style={{ borderTop: "1px solid var(--hairline-soft)", paddingTop: 32 }}>
                <span style={{ fontFamily: "var(--serif)", fontSize: "2.4rem", color: "var(--champagne)", lineHeight: 1 }}>{no}</span>
                <h3 className="h-md" style={{ margin: "18px 0 14px" }}>{t}</h3>
                <p className="muted" style={{ lineHeight: 1.85 }}>{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Listing form (dashboard preview) */}
      <section className="section dark" style={{ paddingTop: "clamp(80px,10vw,140px)" }}>
        <div className="shell">
          <div className="grid" style={{ gridTemplateColumns: "0.9fr 1.1fr", gap: "clamp(48px,6vw,88px)", alignItems: "start" }}>
            <div className="reveal">
              <span className="eyebrow on-dark">Seller dashboard</span>
              <h2 className="h-lg" style={{ marginTop: 24 }}>List an asset</h2>
              <p className="muted" style={{ marginTop: 20, lineHeight: 1.9 }}>
                Start here. Once submitted, your asset enters the Aditi Legal Shield
                queue — you'll get a single, clear status: in review, certified, or
                needs documents. No endless calls.
              </p>
              <div style={{ marginTop: 36, display: "flex", flexDirection: "column", gap: 18 }}>
                {["Upload blueprints, plans & media", "Set your expected price privately", "Track verification status live", "Approve buyer viewings on your terms"].map((x) => (
                  <div key={x} className="flexrow" style={{ gap: 12, alignItems: "flex-start" }}>
                    <span className="gold-text" style={{ marginTop: 3, fontSize: "1.1rem" }}>◈</span><span className="muted" style={{ fontSize: "0.96rem", lineHeight: 1.7 }}>{x}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="reveal" style={{ background: "var(--obsidian-soft)", border: "1px solid var(--obsidian-line)", borderRadius: 3, padding: "clamp(32px,4vw,48px)" }}>
              {sent ? (
                <div className="center" style={{ padding: "40px 0" }}>
                  <span className="badge live" style={{ background: "transparent" }}><span className="dot" /> Submitted for vetting</span>
                  <h3 className="h-md" style={{ margin: "16px 0 8px", color: "var(--paper-ink)" }}>Your asset is in the Legal Shield queue.</h3>
                  <p className="muted">We'll confirm Certified Title Check status within 2 business days.</p>
                </div>
              ) : (
                <form onSubmit={(e) => { e.preventDefault(); setSent(true); }} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
                  <div className="field" style={{ gridColumn: "1 / -1" }}><label>Property title</label><input required placeholder="e.g. Sea-facing villa, ECR" /></div>
                  <div className="field"><label>Asset class</label>
                    <select required defaultValue=""><option value="" disabled>Select…</option><option>Apartments</option><option>Villas</option><option>Houses</option><option>Plots</option><option>Land</option></select>
                  </div>
                  <div className="field"><label>Expected price</label><input required placeholder="₹ in Cr" /></div>
                  <div className="field"><label>City</label><input required placeholder="Hyderabad" /></div>
                  <div className="field"><label>Locality</label><input required placeholder="Jubilee Hills" /></div>
                  <div className="field"><label>Extent</label><input placeholder="sq.ft / acres" /></div>
                  <div className="field"><label>Your role</label>
                    <select defaultValue=""><option value="" disabled>Select…</option><option>Owner</option><option>Developer</option><option>Institution / Estate</option></select>
                  </div>
                  <div className="field" style={{ gridColumn: "1 / -1" }}><label>Blueprints / media</label>
                    <div style={{ border: "1px dashed rgba(255,255,255,0.15)", borderRadius: 3, padding: "32px 24px", textAlign: "center", color: "var(--paper-ink-soft)", fontSize: "0.9rem" }}>
                      Drag files here or click to upload — plans, photos, documents
                    </div>
                  </div>
                  <div className="field" style={{ gridColumn: "1 / -1" }}><label>Contact</label><input required type="email" placeholder="you@email.com" /></div>
                  <button type="submit" className="btn btn-gold" style={{ gridColumn: "1 / -1", justifyContent: "center", marginTop: 8 }}>Submit for vetting <span className="arr">→</span></button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="section-sm">
        <div className="shell center reveal">
          <h2 className="h-md">Buying instead?</h2>
          <p className="muted" style={{ margin: "10px 0 20px" }}>Tell us exactly what you want and we'll match you to live, vetted inventory.</p>
          <Link to="/buy" className="btn btn-ink">Open the Buyer Portal <span className="arr">→</span></Link>
        </div>
      </section>

      <style>{`
        @media (max-width: 860px) {
          .section .grid[style*="repeat(3"] { grid-template-columns: 1fr !important; }
          .section .grid[style*="0.9fr"] { grid-template-columns: 1fr !important; }
          form[style*="1fr 1fr"] { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </>
  );
}
